import {} from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import TimeActionsFormDialog from 'components/domain/time-entry/time-actions-form/TimeActionsFormDialog/TimeActionsFormDialog';
import { ITimeActionsFormData } from 'components/domain/time-entry/time-actions-form/hooks/useTimeActionsForm';
import { useOpenable } from 'hooks';
import _, { isNil } from 'lodash';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { IVisibleTableColumn } from 'types/TableColumn';
import ITimeRange from 'types/TimeRange';
import { remainingDataItemId } from 'utils/constants/utilConstants';
import { ITimeEntryDataTableRow } from '../../TimeEntryDataTable/TimeEntryDataTable';
import TimeEntryDataTableHeader from '../../TimeEntryDataTable/TimeEntryDataTableHeader/TimeEntryDataTableHeader';
import TimeEntryDataReport from '../TimeEntryDataReport';
import useCheckArchivedOfCostCodes from '../hooks/useCheckArchivedOfCostCodes';
import useCheckArchivedOfMembers from '../hooks/useCheckArchivedOfMembers';
import useCheckArchivedOfProject from '../hooks/useCheckArchivedOfProject';
import useCheckCanMangeTimeForMembers from '../hooks/useCheckCanMangeTimeForMembers';
import useCheckDeletedOfEquipment from '../hooks/useCheckDeleteOfEquipment';
import ITimeEntryReportWithHeaderData from '../types/TimeEntryReportWithHeaderData';

export interface ICostCodeTimeEntryDataReportProps {
  className?: string | object;
  timeRange: ITimeRange<DateTime>;
  data: ITimeEntryReportWithHeaderData[];
  selectedIds: string[];
  onSelectChange?: (rows: ITimeEntryDataTableRow[]) => void;
  columnSettings: IVisibleTableColumn[];
  updateCostCodeIds: (costCodeIds: string[]) => void;
  loading: boolean;
  memberIds?: string[] | null;
  projectId?: string | null;
  equipmentId?: string | null;
  loadedAll: boolean;
  applyPrintOptions: boolean;
}

const CostCodeTimeEntryDataReport = ({
  className,
  timeRange,
  data,
  columnSettings,
  selectedIds,
  onSelectChange,
  updateCostCodeIds,
  loading,
  memberIds,
  projectId,
  equipmentId,
  loadedAll,
  applyPrintOptions,
}: ICostCodeTimeEntryDataReportProps) => {
  const timeEntryDialogOpenable = useOpenable();
  const [costCodeId, setCostCodeId] = useState<string | null>(null);
  const memberArchived = useCheckArchivedOfMembers(memberIds);
  const canManageMembers = useCheckCanMangeTimeForMembers(memberIds);
  const checkArchivedOfCostCodes = useCheckArchivedOfCostCodes();
  const checkDeletedOfEquipment = useCheckDeletedOfEquipment();
  const [dialogEquipmentId, setDialogEquipmentId] = useState<string | null | undefined>(null);
  const checkArchivedOfProjects = useCheckArchivedOfProject();
  const [dialogProjectId, setDialogProjectId] = useState<string | null | undefined>(null);

  function renderHeader(item: ITimeEntryReportWithHeaderData) {
    async function handleAddEntry() {
      if (item.id === remainingDataItemId || (await checkArchivedOfCostCodes([item.id]))) {
        setCostCodeId(null);
      } else {
        setCostCodeId(item.id);
      }
      if (await checkDeletedOfEquipment(!isNil(equipmentId) ? [equipmentId] : null)) {
        setDialogEquipmentId(null);
      } else {
        setDialogEquipmentId(equipmentId);
      }
      if (await checkArchivedOfProjects(!isNil(projectId) ? [projectId] : null)) {
        setDialogProjectId(null);
      } else {
        setDialogProjectId(projectId);
      }
      timeEntryDialogOpenable.open();
    }

    return (
      <TimeEntryDataTableHeader
        title={item.title}
        total={item.total}
        openAddEntry={memberArchived === false && canManageMembers ? handleAddEntry : undefined}
      />
    );
  }

  function updateMemberIds(ids: string[]) {
    const costCodeIdsToUpdate: string[] = [];
    data.map((d) => {
      const rowMemberIds = d.rows.map((r) => r.member.id);
      const [matched] = _.partition(rowMemberIds, (rowMemberId) => _.some(ids, (memberId) => memberId === rowMemberId));
      if (!_.isEmpty(matched)) {
        costCodeIdsToUpdate.push(d.id);
      }
    });
    const uniqIds = _.uniq(costCodeIdsToUpdate);
    updateCostCodeIds(_.isEmpty(uniqIds) && costCodeId ? [costCodeId] : uniqIds);
    setCostCodeId(null);
  }

  function handleAddEntrySubmit(data: ITimeActionsFormData) {
    updateMemberIds(data.members);
    timeEntryDialogOpenable.close();
  }

  return (
    <>
      <TimeEntryDataReport
        data={data}
        loading={loading}
        columnSettings={columnSettings}
        className={classNames('cost-code-time-entry-data-report', className)}
        selectedIds={selectedIds}
        onSelectChange={onSelectChange}
        timeRange={timeRange}
        renderHeader={renderHeader}
        onDataChange={updateMemberIds}
        loadedAll={loadedAll}
        applyPrintOptions={applyPrintOptions}
      />
      <TimeActionsFormDialog
        type={'add'}
        currentDate={timeRange.endTime}
        costCodeId={costCodeId}
        projectId={dialogProjectId}
        memberIds={memberIds ?? []}
        equipmentId={dialogEquipmentId}
        isOpen={timeEntryDialogOpenable.isOpen}
        onClose={timeEntryDialogOpenable.close}
        onSubmit={handleAddEntrySubmit}
        onDelete={handleAddEntrySubmit}
      />
    </>
  );
};

CostCodeTimeEntryDataReport.defaultProps = {
  applyPrintOptions: true,
};

export default CostCodeTimeEntryDataReport;
